import { db, functions } from '../config/firebase';
import { collection, query, getDocs, doc, getDoc, Timestamp, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { format, parseISO } from 'date-fns';
import { toast } from '../components/ui/use-toast';

// Cache storage
let metricsCache: DayAwayMetrics | null = null;
let ledgerEntriesCache: TimeOffEntry[] | null = null;
let lastFetchTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// NEW: Calendar-specific caches
let calendarThreeMonthCache: TimeOffEntry[] | null = null;
let calendarThreeMonthCacheTime = 0;
let calendarMonthCache: Map<string, TimeOffEntry[]> = new Map();
let calendarMonthCacheTime: Map<string, number> = new Map();
const CALENDAR_CACHE_TTL = 10 * 60 * 1000; // 10 minutes for calendar data

// Flag to track if we've already shown the sync notification
let syncNotificationShown = false;

export interface TimeOffEntry {
  id: string;
  date: string; // ISO format: YYYY-MM-DD
  employee: string;
  timeOffType: 'Full' | 'Half' | 'Email Only';
  note: string;
  status: string;
  timestamp?: Timestamp;
  isTeamMember?: boolean;
}

export interface DayAwayMetrics {
  offToday: number;
  offTomorrow: number;
  totalEntries: number;
  daysThisYear: number;
  lastUpdated: Timestamp;
}

export interface DashboardData {
  upcomingTimeOff: TimeOffEntry[];
  calendarData: {
    date: string;
    employees: TimeOffEntry[];
  }[];
}


// Helper function to check if an employee is in the user's team
async function isTeamMember(employee: string): Promise<boolean> {
  try {
    const userEmail = localStorage.getItem('userEmail') || '';
    const snapshot = await getDocs(
      query(
        collection(db, 'teams'),
        where('owner', '==', userEmail)
      )
    );

    if (snapshot.empty) return false;

    const teamDoc = snapshot.docs[0];
    const members = teamDoc.data().members || [];
    return members.some((member: { name: string }) => member.name === employee);
  } catch (error) {
    
    return false;
  }
}

export async function getEmployeeNames(): Promise<string[]> {
  try {
    
    
    // Use Firebase callable function to get names from Data Validation sheet
    const getEmployeeNamesFunction = httpsCallable(functions, 'getEmployeeNames');
    const result = await getEmployeeNamesFunction();
    
    
    
    // Cast the result data to our interface
    const namesResult = result.data as { success: boolean; names: string[]; error?: string };
    
    if (namesResult.success) {
      
      return namesResult.names;
    } else {
      
      throw new Error(namesResult.error || 'Unknown error from getEmployeeNames function');
    }
  } catch (error) {
    
    return [];
  }
}

// Interface for Dashboard Sheet data
interface DashboardSheetRow {
  person: string;
  allTime: {
    full: number;
    half: number;
    emailOnly: number;
    total: number;
  };
  thisYear: {
    full: number;
    half: number;
    emailOnly: number;
    total: number;
  };
  lastYear: {
    full: number;
    half: number;
    emailOnly: number;
    total: number;
  };
}

export async function getDashboardSheetData(): Promise<DashboardSheetRow[]> {
  try {
    
    
    // Use Firebase callable function to get data from Dashboard sheet
    const getDashboardSheetDataFunction = httpsCallable(functions, 'getDashboardSheetData');
    const result = await getDashboardSheetDataFunction();
    
    
    
    // Cast the result data to our interface
    const dashboardResult = result.data as { success: boolean; data: DashboardSheetRow[]; message?: string };
    
    if (dashboardResult.success) {
      
      return dashboardResult.data;
    } else {
      
      throw new Error(dashboardResult.message || 'Unknown error from getDashboardSheetData function');
    }
  } catch (error) {
    
    return [];
  }
}

export async function submitTimeOff(request: { date: string; name: string; type: string; note: string }): Promise<void> {
  try {
    
    
    // Use Firebase callable function for submission
    const submitTimeOffFunction = httpsCallable(functions, 'submitTimeOff');
    const result = await submitTimeOffFunction({
      date: request.date,
      name: request.name,
      type: request.type,
      note: request.note || ''
    });
    
    
    
    // Cast the result data to our interface
    const submissionResult = result.data as { success: boolean; message: string; error?: string };
    
    if (submissionResult.success) {
      // Clear cache after successful submission to force reload
      ledgerEntriesCache = null;
      metricsCache = null;
      lastFetchTime = 0;
      
      
    } else {
      
      throw new Error(submissionResult.error || 'Unknown error from submission function');
    }
  } catch (error) {
    
    throw error;
  }
}

const dayAwayService = {
  getEmployeeNames,
  submitTimeOff,
  getDashboardSheetData,
  // Check if we're in development mode but not using Netlify Dev
  isLocalDevelopment(): boolean {
    // Always return false to ensure we use Firebase and Netlify functions
    // This fixes the issue where data wasn't being synced to Firebase
    return false;
  },

  // Flag to force bypassing cache after sync
  _forceRefresh: false,

  // Flag to enable optimized loading (progressive historical + active)
  _useOptimizedLoading: true,

  // OPTIMIZED: Get historical data from cache (instant loading)
  async getHistoricalCache(): Promise<TimeOffEntry[]> {
    try {
      console.log('Loading historical data from cache...');
      
      const cacheRef = doc(db, 'timeOffHistoricalCache', 'current');
      const cacheDoc = await getDoc(cacheRef);
      
      if (!cacheDoc.exists()) {
        console.log('No historical cache found, triggering historical sync...');
        // Trigger historical sync in background
        this.syncHistoricalDataOnly();
        return [];
      }
      
      const cacheData = cacheDoc.data();
      const entries = cacheData.entries || [];
      
      console.log(`Loaded ${entries.length} historical entries from cache (instant)`);
      return entries;
    } catch (error) {
      console.error('Error loading historical cache:', error);
      return [];
    }
  },

  // OPTIMIZED: Sync only active data (fast - 3-5 seconds)
  async syncActiveDataOnly(): Promise<boolean> {
    try {
      console.log('Syncing active data only...');
      
      const syncActiveDataOnlyFunction = httpsCallable(functions, 'syncActiveDataOnly');
      const result = await syncActiveDataOnlyFunction();
      
      const syncResult = result.data as {
        success: boolean;
        message: string;
        stats?: {
          totalActiveEntries: number;
          newEntries: number;
          updatedEntries: number;
          offToday: number;
          offTomorrow: number;
        };
        error?: string;
      };
      
      if (syncResult.success) {
        // Clear cache to force reload from Firebase
        ledgerEntriesCache = null;
        metricsCache = null;
        lastFetchTime = 0;
        this._forceRefresh = true;
        
        console.log(`Active sync completed: ${syncResult.message}`);
        return true;
      } else {
        console.error('Active sync failed:', syncResult.error);
        return false;
      }
    } catch (error) {
      console.error('Error in active sync:', error);
      return false;
    }
  },

  // OPTIMIZED: Sync historical data and build cache (run periodically)
  async syncHistoricalDataOnly(): Promise<boolean> {
    try {
      console.log('Syncing historical data and building cache...');
      
      const syncHistoricalDataFunction = httpsCallable(functions, 'syncHistoricalData');
      const result = await syncHistoricalDataFunction();
      
      const syncResult = result.data as {
        success: boolean;
        message: string;
        stats?: {
          totalHistoricalEntries: number;
          cacheVersion: string;
          boundaryDate: string;
          dataReduction: string;
        };
        error?: string;
      };
      
      if (syncResult.success) {
        console.log(`Historical sync completed: ${syncResult.message}`);
        return true;
      } else {
        console.error('Historical sync failed:', syncResult.error);
        return false;
      }
    } catch (error) {
      console.error('Error in historical sync:', error);
      return false;
    }
  },

  // OPTIMIZED: Get active entries only from Firebase
  async getActiveEntries(): Promise<TimeOffEntry[]> {
    try {
      console.log('Loading active entries from Firebase...');
      
      const entriesRef = collection(db, 'timeOffEntries');
      const q = query(entriesRef, where('status', '==', 'Good-to-Go'));
      const snapshot = await getDocs(q);
      
      const entries = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        } as TimeOffEntry;
      });
      
      console.log(`Loaded ${entries.length} active entries from Firebase`);
      return entries;
    } catch (error) {
      console.error('Error loading active entries:', error);
      return [];
    }
  },

  // OPTIMIZED: Progressive loading - instant historical + fast active
  async getLedgerEntriesOptimized(): Promise<TimeOffEntry[]> {
    try {
      console.log('Starting optimized progressive loading...');
      
      // Step 1: Load historical cache instantly
      const historicalEntries = await this.getHistoricalCache();
      
      // Step 2: Sync active data (fast)
      await this.syncActiveDataOnly();
      
      // Step 3: Get fresh active entries
      const activeEntries = await this.getActiveEntries();
      
      // Step 4: Merge historical + active data
      const allEntries = [...historicalEntries, ...activeEntries];
      
      console.log(`Optimized loading complete: ${historicalEntries.length} historical + ${activeEntries.length} active = ${allEntries.length} total entries`);
      
      // Update cache
      ledgerEntriesCache = allEntries;
      lastFetchTime = Date.now();
      
      return allEntries;
    } catch (error) {
      console.error('Error in optimized loading:', error);
      // Fallback to original method
      return this.getLedgerEntries();
    }
  },

  // Trigger a manual sync from Google Sheets to Firebase
  async triggerSync(): Promise<boolean> {
    try {
      // Show sync in progress notification
      toast({
        title: "Syncing Data",
        description: "Syncing data from Google Sheets to Firebase. This may take up to 10 minutes...",
        duration: 8000,
      });
      
      // Use Netlify function for sync instead of Google Cloud Function
      const response = await fetch('/.netlify/functions/dayAway', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'sync' }),
      });
      
      // Check if response is ok
      if (!response.ok) {
        throw new Error(`Sync function returned status ${response.status}: ${response.statusText}`);
      }
      
      // Parse the JSON response
      const result = await response.json();
      
      // Handle the response from the Netlify function
      if (result.success) {
        // Clear cache to force reload from Firebase
        ledgerEntriesCache = null;
        metricsCache = null;
        lastFetchTime = 0;
        this._forceRefresh = true;
        
        // Also force a cache bust by setting a flag
        syncNotificationShown = false;
        
        toast({
          title: "Sync Successful",
          description: `Sync completed successfully. Found ${result.entriesCount || 0} entries.`,
        });
        
        return true;
      } else {
        throw new Error(result.error || 'Unknown error from sync function');
      }
    } catch (error) {
      // Handle specific errors
      let errorMessage = "Unknown error occurred";
      if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          errorMessage = "Sync is taking longer than expected. The data may still be syncing in the background. Please wait a few minutes and refresh the page.";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Sync Issue",
        description: errorMessage,
        variant: "destructive",
        duration: 10000
      });
      return false;
    }
  },
  
  // Fetch entries directly from Google Sheets as a fallback
  async fetchEntriesFromSheets(): Promise<TimeOffEntry[]> {
    try {
      // Check if we're running on localhost
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isLocalhost) {
        
        return [];
      }
      
      const response = await fetch('/.netlify/functions/dayAway', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'sync' })
      });
      
      const result = await response.json();
      
      if (result.success && result.entries && Array.isArray(result.entries)) {
        // Define the entry type
        interface SheetEntry {
          date: string;
          name: string;
          type?: string;
          note?: string;
        }
        
        // Convert to TimeOffEntry format with proper type handling
        const mappedEntries = result.entries
          .filter((entry: SheetEntry) => {
            // Filter out invalid entries silently instead of logging warnings
            return entry && entry.date && entry.name;
          })
          .map((entry: SheetEntry) => {
            try {
              // Parse the date safely
              let formattedDate;
              try {
                formattedDate = format(new Date(entry.date), 'yyyy-MM-dd');
              } catch (dateError) {
                // If date parsing fails, try to handle common formats
                const parts = entry.date.split('/');
                if (parts.length === 3) {
                  const month = parseInt(parts[0], 10);
                  const day = parseInt(parts[1], 10);
                  let year = parseInt(parts[2], 10);
                  
                  // Handle 2-digit year
                  if (year < 100) {
                    year += year < 50 ? 2000 : 1900;
                  }
                  
                  formattedDate = format(new Date(year, month - 1, day), 'yyyy-MM-dd');
                } else {
                  // If we can't parse the date, skip this entry
                  return null;
                }
              }
              
              return {
                id: `${entry.date}-${entry.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`,
                date: formattedDate,
                employee: entry.name,
                timeOffType: entry.type || '',
                note: entry.note || '',
                status: '',
                timestamp: Timestamp.now()
              } as TimeOffEntry;
            } catch (err) {
              // Silently skip problematic entries instead of logging errors
              return null;
            }
          });
        
        // Filter out null entries with proper type handling
        return mappedEntries.filter((entry: unknown): entry is TimeOffEntry => entry !== null);
      }
      return [];
    } catch (error) {
      
      return [];
    }
  },
  
  async getLedgerEntries(): Promise<TimeOffEntry[]> {
    const checkTeamMembership = async (entries: TimeOffEntry[]): Promise<TimeOffEntry[]> => {
      return Promise.all(entries.map(async entry => ({
        ...entry,
        isTeamMember: await isTeamMember(entry.employee)
      })));
    };

    try {
      // Use optimized loading if enabled
      if (this._useOptimizedLoading) {
        console.log('Using optimized progressive loading...');
        const entries = await this.getLedgerEntriesOptimized();
        return checkTeamMembership(entries);
      }

      // Return cached entries if they exist and are not expired (unless force refresh is set)
      const now = Date.now();
      if (ledgerEntriesCache && (now - lastFetchTime) < CACHE_TTL && !this._forceRefresh) {
        
        return ledgerEntriesCache;
      }
      
      // Reset force refresh flag
      if (this._forceRefresh) {
        this._forceRefresh = false;
      }


      
      
      // Try to get entries from Firebase - exclude placeholder documents
      const entriesRef = collection(db, 'timeOffEntries');
      const q = query(entriesRef, where('id', '!=', '_placeholder'));
      const snapshot = await getDocs(q);
      
      // Log the number of documents found
      
      
      if (snapshot.empty || snapshot.size === 0) {
        
        
        // Show notification only once per session
        if (!syncNotificationShown) {
          toast({
            title: "Initializing Data",
            description: "Setting up DayAway data. This may take a moment...",
            duration: 5000,
          });
          syncNotificationShown = true;
        }
        
        // Trigger a sync
        await this.triggerSync();
        
        // Try Firebase again with a short delay to allow sync to complete
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        
        const retrySnapshot = await getDocs(q);
        
        
        if (retrySnapshot.empty || retrySnapshot.size === 0) {
          
          // If still empty, fall back to direct Sheets access as a last resort
          const sheetsEntries = await this.fetchEntriesFromSheets();
          const entriesWithTeam = await checkTeamMembership(sheetsEntries);
          
          // Update cache
          ledgerEntriesCache = entriesWithTeam;
          lastFetchTime = now;
          
          return entriesWithTeam;
        }
        
        // Process entries from retry
        const entries = retrySnapshot.docs
          .filter(doc => doc.id !== '_placeholder') // Extra filter to be safe
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data
            } as TimeOffEntry;
          });
        
        
        
        // Add team membership info
        const entriesWithTeam = await checkTeamMembership(entries);
        
        // Update cache
        ledgerEntriesCache = entriesWithTeam;
        lastFetchTime = now;
        
        return entriesWithTeam;
      }
      
      // Process entries from successful first attempt
      const entries = snapshot.docs
        .filter(doc => doc.id !== '_placeholder') // Extra filter to be safe
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data
          } as TimeOffEntry;
        });
      
      
      
      // Add team membership info
      const entriesWithTeam = await checkTeamMembership(entries);
      
      // Update cache
      ledgerEntriesCache = entriesWithTeam;
      lastFetchTime = now;
      
      return entriesWithTeam;
    } catch (error) {
      
      
      // Check if we have cached entries before falling back to Sheets
      if (ledgerEntriesCache) {
        
        return ledgerEntriesCache;
      }
      
      // Try fallback to direct Sheets access as a last resort
      
      try {
        const entries = await this.fetchEntriesFromSheets();
        const entriesWithTeam = await checkTeamMembership(entries);
        
        // Update cache
        ledgerEntriesCache = entriesWithTeam;
        lastFetchTime = Date.now();
        
        return entriesWithTeam;
      } catch (fallbackError) {
        
        return [];
      }
    }
  },

  async getDashboardData(): Promise<{
    upcomingTimeOff: TimeOffEntry[];
    calendarData: { date: string; employees: TimeOffEntry[]; }[];
  }> {
    try {
      const entries = await this.getLedgerEntries();
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      
      // Get upcoming time off for the next 7 days
      const upcomingTimeOff = entries.filter(entry => {
        try {
          const entryDate = parseISO(entry.date);
          const sevenDaysFromNow = new Date();
          sevenDaysFromNow.setDate(now.getDate() + 7);
          return entryDate >= now && entryDate <= sevenDaysFromNow;
        } catch (err) {
          return false;
        }
      }).sort((a, b) => parseISO(a.date).getTime() - parseISO(b.date).getTime());

      // Group entries by date
      const calendarData = entries.reduce((acc: { [key: string]: TimeOffEntry[] }, entry) => {
        if (!acc[entry.date]) {
          acc[entry.date] = [];
        }
        acc[entry.date].push(entry);
        return acc;
      }, {});

      return {
        upcomingTimeOff,
        calendarData: Object.entries(calendarData).map(([date, employees]) => ({
          date,
          employees
        }))
      };
    } catch (error) {
      
      return {
        upcomingTimeOff: [],
        calendarData: []
      };
    }
  },

  // Helper function to calculate metrics from entries
  async calculateMetricsFromEntries(entries: TimeOffEntry[]): Promise<DayAwayMetrics> {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const todayStr = today.toISOString().split('T')[0];
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    
    const offToday = entries.filter(entry => entry.date === todayStr).length;
    const offTomorrow = entries.filter(entry => entry.date === tomorrowStr).length;
    
    // Calculate days this year (approximate)
    const currentYear = today.getFullYear();
    const startOfYear = new Date(currentYear, 0, 1).toISOString().split('T')[0];
    const endOfYear = new Date(currentYear, 11, 31).toISOString().split('T')[0];
    
    const entriesThisYear = entries.filter(
      entry => entry.date >= startOfYear && entry.date <= endOfYear
    );
    
    // Count unique days with at least one person off
    const uniqueDaysOff = new Set(entriesThisYear.map(entry => entry.date)).size;
    
    return {
      offToday,
      offTomorrow,
      totalEntries: entries.length,
      daysThisYear: uniqueDaysOff,
      lastUpdated: Timestamp.now()
    };
  },

  async getMetrics(): Promise<DayAwayMetrics> {
    try {
      // Return cached metrics if they exist and are not expired (unless force refresh is set)
      const now = Date.now();
      if (metricsCache && (now - lastFetchTime) < CACHE_TTL && !this._forceRefresh) {
        
        return metricsCache;
      }


      
      const docRef = doc(db, 'dayAwayMetrics', 'current');
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        
        
        // Show notification only once per session
        if (!syncNotificationShown) {
          toast({
            title: "Initializing Data",
            description: "Setting up DayAway data. This may take a moment...",
            duration: 5000,
          });
          syncNotificationShown = true;
        }
        
        // Trigger a sync
        await this.triggerSync();
        
        // Try Firebase again with a short delay to allow sync to complete
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        
        const retryDocSnap = await getDoc(docRef);
        
        if (!retryDocSnap.exists()) {
          
          // If still not found, calculate metrics from entries
          const entries = await this.getLedgerEntries();
          const calculatedMetrics = await this.calculateMetricsFromEntries(entries);
          
          
          
          // Update cache
          metricsCache = calculatedMetrics;
          lastFetchTime = now;
          
          return calculatedMetrics;
        }
        
        
        const metrics = retryDocSnap.data() as DayAwayMetrics;
        
        // Update cache
        metricsCache = metrics;
        lastFetchTime = now;
        
        return metrics;
      }

      
      const metrics = docSnap.data() as DayAwayMetrics;

      // Update cache
      metricsCache = metrics;
      lastFetchTime = now;

      return metrics;
    } catch (error) {
      
      
      // Check if we have cached metrics before calculating from entries
      if (metricsCache) {
        
        return metricsCache;
      }
      
      // Try to calculate metrics from entries as a fallback
      
      try {
        const entries = await this.getLedgerEntries();
        const calculatedMetrics = await this.calculateMetricsFromEntries(entries);
        
        
        
        // Update cache
        metricsCache = calculatedMetrics;
        lastFetchTime = Date.now();
        
        return calculatedMetrics;
      } catch (fallbackError) {
        
        return {
          offToday: 0,
          offTomorrow: 0,
          totalEntries: 0,
          daysThisYear: 0,
          lastUpdated: Timestamp.now()
        };
      }
    }
  },

  // OPTIMIZED: Get team-specific time off data only
  async getTeamTimeOffData(teamMemberNames: string[]): Promise<TimeOffEntry[]> {
    try {
      if (!teamMemberNames || teamMemberNames.length === 0) {
        console.log('No team members provided, returning empty array');
        return [];
      }

      console.log(`Loading time off data for ${teamMemberNames.length} team members:`, teamMemberNames);
      
      // Get entries from Firebase filtered by team member names
      const entriesRef = collection(db, 'timeOffEntries');
      const q = query(
        entriesRef,
        where('employee', 'in', teamMemberNames),
        where('status', '==', 'Good-to-Go')
      );
      const snapshot = await getDocs(q);
      
      const entries = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          isTeamMember: true // All entries are for team members
        } as TimeOffEntry;
      });
      
      console.log(`Loaded ${entries.length} time off entries for team members (optimized)`);
      return entries;
    } catch (error) {
      console.error('Error loading team time off data:', error);
      return [];
    }
  },

  // OPTIMIZED: Get employee names with lazy loading (only when needed)
  async getEmployeeNamesLazy(): Promise<string[]> {
    try {
      // Check if we have cached employee names
      const cachedNames = sessionStorage.getItem('employeeNames');
      if (cachedNames) {
        const parsed = JSON.parse(cachedNames);
        console.log(`Using cached employee names: ${parsed.length} names`);
        return parsed;
      }
      
      console.log('Loading employee names (lazy)...');
      const names = await this.getEmployeeNames();
      
      // Cache for session
      sessionStorage.setItem('employeeNames', JSON.stringify(names));
      console.log(`Cached ${names.length} employee names for session`);
      
      return names;
    } catch (error) {
      console.error('Error in lazy employee names loading:', error);
      return [];
    }
  },

  // NEW: Calendar-optimized functions
  
  // Helper function to get month key (YYYY-MM format)
  getMonthKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  },

  // Helper function to get date range for a month
  getMonthDateRange(year: number, month: number): { start: string; end: string } {
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0); // Last day of month
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  },

  // NEW: Fetch current month + next 2 months data (fast initial load)
  async getCalendarThreeMonthData(): Promise<TimeOffEntry[]> {
    try {
      const now = Date.now();
      
      // Check cache first
      if (calendarThreeMonthCache && (now - calendarThreeMonthCacheTime) < CALENDAR_CACHE_TTL) {
        console.log('Using cached 3-month calendar data');
        return calendarThreeMonthCache;
      }

      console.log('Fetching current month + next 2 months calendar data...');
      
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();
      
      // Calculate date ranges for current month + next 2 months
      const ranges = [];
      for (let i = 0; i < 3; i++) {
        const targetDate = new Date(currentYear, currentMonth + i, 1);
        const range = this.getMonthDateRange(targetDate.getFullYear(), targetDate.getMonth());
        ranges.push(range);
      }
      
      // Get the overall date range
      const startDate = ranges[0].start;
      const endDate = ranges[2].end;
      
      console.log(`Loading calendar data from ${startDate} to ${endDate}`);
      
      // Query Firestore for entries in this date range - simplified to avoid index issues
      const entriesRef = collection(db, 'timeOffEntries');
      const q = query(
        entriesRef,
        where('date', '>=', startDate),
        where('date', '<=', endDate)
      );
      
      const snapshot = await getDocs(q);
      const entries = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        } as TimeOffEntry;
      });
      
      // Add team membership info
      const entriesWithTeam = await Promise.all(entries.map(async entry => ({
        ...entry,
        isTeamMember: await isTeamMember(entry.employee)
      })));
      
      // Cache the results
      calendarThreeMonthCache = entriesWithTeam;
      calendarThreeMonthCacheTime = now;
      
      // Also cache individual months for faster navigation
      for (let i = 0; i < 3; i++) {
        const targetDate = new Date(currentYear, currentMonth + i, 1);
        const monthKey = this.getMonthKey(targetDate);
        const range = ranges[i];
        
        const monthEntries = entriesWithTeam.filter(entry =>
          entry.date >= range.start && entry.date <= range.end
        );
        
        calendarMonthCache.set(monthKey, monthEntries);
        calendarMonthCacheTime.set(monthKey, now);
      }
      
      console.log(`Loaded ${entriesWithTeam.length} entries for 3-month calendar view (optimized)`);
      return entriesWithTeam;
      
    } catch (error) {
      console.error('Error fetching 3-month calendar data:', error);
      return [];
    }
  },

  // NEW: Fetch individual month data on-demand (for navigation)
  async getCalendarMonthData(year: number, month: number): Promise<TimeOffEntry[]> {
    try {
      const monthKey = this.getMonthKey(new Date(year, month, 1));
      const now = Date.now();
      
      // Check cache first
      const cachedData = calendarMonthCache.get(monthKey);
      const cacheTime = calendarMonthCacheTime.get(monthKey) || 0;
      
      if (cachedData && (now - cacheTime) < CALENDAR_CACHE_TTL) {
        console.log(`Using cached data for month ${monthKey}`);
        return cachedData;
      }

      console.log(`Fetching calendar data for month ${monthKey}...`);
      
      const range = this.getMonthDateRange(year, month);
      
      // Query Firestore for entries in this month - simplified to avoid index issues
      const entriesRef = collection(db, 'timeOffEntries');
      const q = query(
        entriesRef,
        where('date', '>=', range.start),
        where('date', '<=', range.end)
      );
      
      const snapshot = await getDocs(q);
      const entries = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        } as TimeOffEntry;
      });
      
      // Add team membership info
      const entriesWithTeam = await Promise.all(entries.map(async entry => ({
        ...entry,
        isTeamMember: await isTeamMember(entry.employee)
      })));
      
      // Cache the results
      calendarMonthCache.set(monthKey, entriesWithTeam);
      calendarMonthCacheTime.set(monthKey, now);
      
      console.log(`Loaded ${entriesWithTeam.length} entries for month ${monthKey} (on-demand)`);
      return entriesWithTeam;
      
    } catch (error) {
      console.error(`Error fetching calendar data for month ${year}-${month + 1}:`, error);
      return [];
    }
  },

  // NEW: Get calendar dashboard data (optimized version)
  async getCalendarDashboardData(): Promise<DashboardData> {
    try {
      // Get the 3-month data (current + next 2 months)
      const entries = await this.getCalendarThreeMonthData();
      
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      
      // Get upcoming time off for the next 7 days
      const upcomingTimeOff = entries.filter(entry => {
        try {
          const entryDate = parseISO(entry.date);
          const sevenDaysFromNow = new Date();
          sevenDaysFromNow.setDate(now.getDate() + 7);
          return entryDate >= now && entryDate <= sevenDaysFromNow;
        } catch (err) {
          return false;
        }
      }).sort((a, b) => parseISO(a.date).getTime() - parseISO(b.date).getTime());

      // Group entries by date for calendar display
      const calendarData = entries.reduce((acc: { [key: string]: TimeOffEntry[] }, entry) => {
        if (!acc[entry.date]) {
          acc[entry.date] = [];
        }
        acc[entry.date].push(entry);
        return acc;
      }, {});

      return {
        upcomingTimeOff,
        calendarData: Object.entries(calendarData).map(([date, employees]) => ({
          date,
          employees
        }))
      };
    } catch (error) {
      console.error('Error getting calendar dashboard data:', error);
      return {
        upcomingTimeOff: [],
        calendarData: []
      };
    }
  },

  // NEW: Clear calendar caches (for manual sync)
  clearCalendarCaches(): void {
    console.log('Clearing calendar caches...');
    calendarThreeMonthCache = null;
    calendarThreeMonthCacheTime = 0;
    calendarMonthCache.clear();
    calendarMonthCacheTime.clear();
  },

  // NEW: Manual sync for calendar (clears caches and refetches 3-month data)
  async syncCalendarData(): Promise<boolean> {
    try {
      console.log('Manual calendar sync initiated...');
      
      // Clear all calendar caches
      this.clearCalendarCaches();
      
      // Show sync notification
      toast({
        title: "Syncing Calendar Data",
        description: "Refreshing calendar data from server...",
        duration: 3000,
      });
      
      // Fetch fresh 3-month data
      const freshData = await this.getCalendarThreeMonthData();
      
      if (freshData.length > 0) {
        toast({
          title: "Calendar Sync Complete",
          description: `Loaded ${freshData.length} calendar entries`,
        });
        return true;
      } else {
        toast({
          title: "Calendar Sync Warning",
          description: "No calendar data found for the current period",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error('Error in calendar sync:', error);
      toast({
        title: "Calendar Sync Failed",
        description: "Failed to sync calendar data. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  },

  // Get sync status from dayAwayMetrics
  async getSyncStatus(): Promise<{ lastSync: Date | null }> {
    try {
      const docRef = doc(db, 'dayAwayMetrics', 'current');
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return { lastSync: null };
      }
      
      const data = docSnap.data();
      const lastUpdated = data.lastUpdated;
      
      if (lastUpdated && lastUpdated.toDate) {
        return { lastSync: lastUpdated.toDate() };
      }
      
      return { lastSync: null };
    } catch (error) {
      console.error('Error fetching sync status:', error);
      return { lastSync: null };
    }
  },

};

export default dayAwayService;
